import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Loader, Pencil, RefreshCw, Search } from "lucide-react";
import { toast } from "react-hot-toast";
import CustomSelect from "../../../components/ui/CustomSelect";
import { useConsumerPayoutMethods, useSyncConsumerPayoutMethods } from "../hooks/useConsumerPayoutMethods";
import { useCurrencies } from "../../currency-settings/hooks/useCurrencies";
import { buildCurrencyOptions, parseCurrencyOption } from "../../../utils/currencyOptions";
import ConsumerPayoutMethodEditForm from "./ConsumerPayoutMethodEditForm";

export function ConsumerPayoutMethodTable() {
  const [search, setSearch] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const { currencyCode, countryCode: filterCountryCode } = parseCurrencyOption(currencyFilter);
  const { data, isLoading, isError, error } = useConsumerPayoutMethods(
    search.trim() || undefined,
    currencyCode || undefined
  );
  const { data: currenciesResponse } = useCurrencies();
  const currencies = currenciesResponse?.data || [];
  const currencyOptions = buildCurrencyOptions(currencies);

  const [selected, setSelected] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { mutate: syncMethods, isPending: isSyncing } = useSyncConsumerPayoutMethods();

  // Pulls the business payout methods in now (they are also pulled every night):
  // new ones appear, and ones switched off at business level are switched off
  // here too. A country filter, when picked, syncs just that country.
  const handleSync = () => {
    syncMethods(filterCountryCode || undefined, {
      onSuccess: (res) => {
        const r = res?.data;
        if (r?.started === false) return toast.error(r.reason || "A sync is already running");
        if (r?.finished && r.summary) {
          const s = r.summary;
          toast.success(
            `Synced ${filterCountryCode}: ${s.created} new, ${s.enabled} turned on, ${s.disabled} turned off, ${s.softDeleted} removed` +
              (s.failed?.length ? `, failed: ${s.failed.join(", ")}` : "")
          );
        } else {
          toast.success("Sync started for all countries. It takes about a minute; refresh the list afterwards.");
        }
      },
      // Say what actually came back (status and message), so a missing route, a
      // gateway timeout and a network block can be told apart.
      onError: (err) => {
        const status = err?.response?.status;
        const message = err?.response?.data?.message;
        const detail = [status ? `HTTP ${status}` : "no response from the server", Array.isArray(message) ? message.join(", ") : message || err?.message]
          .filter(Boolean)
          .join(": ");
        console.error("Payout method sync failed", err);
        toast.error(`Could not start the sync (${detail})`);
      },
    });
  };

  const handleEdit = (row) => {
    setSelected(row);
    setIsEditOpen(true);
  };

  const columns = [
    {
      id: "currencyCode",
      header: "Currency",
      accessorFn: (row) => row.currencyCode,
      cell: (info) => (
        <span className="font-semibold text-gray-800 uppercase">{info.getValue()}</span>
      ),
    },
    {
      id: "countryCode",
      header: "Country",
      accessorFn: (row) => row.countryCode,
      cell: (info) => <span className="text-sm text-gray-600 uppercase">{info.getValue()}</span>,
    },
    {
      id: "name",
      header: "Payout Method",
      accessorFn: (row) => row.name,
      cell: (info) => <span className="text-sm font-medium text-gray-700">{info.getValue() || "—"}</span>,
    },
    {
      id: "availability",
      header: "Availability",
      accessorFn: (row) => row.isEnabled,
      cell: (info) =>
        info.getValue() === false ? (
          <span className="py-1 px-2 text-xs font-medium text-red-700 bg-red-50 rounded-full whitespace-nowrap" title="The business side no longer offers this method, so customers cannot use it.">
            Off at business level
          </span>
        ) : (
          <span className="py-1 px-2 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full whitespace-nowrap">On</span>
        ),
    },
    {
      id: "category",
      header: "Category",
      accessorFn: (row) => row.category,
      cell: (info) => <span className="text-sm text-gray-600">{info.getValue() || "—"}</span>,
    },
    {
      id: "fee",
      header: "Payout Fee",
      accessorFn: (row) => row,
      cell: (info) => {
        const row = info.getValue();
        const value = Number(row.payoutFeeValue ?? 0);
        return (
          <span className="text-sm text-gray-600">
            {row.payoutFeeType === "percentage" ? `${value}%` : value}
          </span>
        );
      },
    },
    {
      id: "platformFee",
      header: "Platform Fee",
      accessorFn: (row) => row.platformFeeValue,
      cell: (info) => (
        <span className="text-sm text-gray-600">{Number(info.getValue() ?? 0)}</span>
      ),
    },
    {
      id: "minPayoutPlatformFee",
      header: "Min Platform Fee",
      accessorFn: (row) => row.minPayoutPlatformFee,
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="text-sm text-gray-500">
            {value === null || value === undefined ? "Currency default" : Number(value)}
          </span>
        );
      },
    },
    {
      id: "amountLimits",
      header: "Amount Limits",
      accessorFn: (row) => row,
      cell: (info) => {
        const row = info.getValue();
        const min = row.minPayoutValue;
        const max = row.maxPayoutValue;
        if ((min === null || min === undefined) && (max === null || max === undefined)) {
          return <span className="text-sm text-gray-500">No limit</span>;
        }
        return (
          <span className="text-sm text-gray-600">
            {min === null || min === undefined ? "—" : Number(min)}
            {" – "}
            {max === null || max === undefined ? "—" : Number(max)}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <button
          onClick={() => handleEdit(row.original)}
          className="flex gap-1 items-center text-sm font-medium text-emerald-600 hover:text-emerald-800"
        >
          <Pencil size={14} /> Edit
        </button>
      ),
    },
  ];

  const records = useMemo(() => {
    const rows = data?.data || [];
    return filterCountryCode
      ? rows.filter((r) => String(r.countryCode || "").toUpperCase() === filterCountryCode)
      : rows;
  }, [data, filterCountryCode]);

  const table = useReactTable({
    data: records,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isError) {
    return (
      <div className="flex flex-1 justify-center items-center h-48">
        <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
          Error: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex gap-3 items-center mb-4">
        <div className="relative max-w-xs w-full">
          <Search className="absolute top-1/2 left-3 w-4 h-4 text-gray-400 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, country, or currency"
            className="py-2 pr-3 pl-9 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div className="w-48">
          <CustomSelect
            value={currencyFilter}
            onChange={setCurrencyFilter}
            options={currencyOptions}
            placeholder="Filter by Currency"
          />
        </div>
        <button
          onClick={handleSync}
          disabled={isSyncing}
          title="Pull the business payout methods in now. They are also pulled every night. Picking a currency syncs just that country."
          className="flex gap-2 items-center py-2 px-3 ml-auto text-sm font-bold text-emerald-700 bg-white rounded-lg border-2 border-emerald-500 hover:bg-emerald-50 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`} />
          {isSyncing ? "Syncing…" : filterCountryCode ? `Sync ${filterCountryCode} now` : "Sync all now"}
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-[400px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading consumer payout methods <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto min-h-[300px]">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-gray-50/50">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      {search ? `No payout methods match "${search}"` : "No payout methods found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConsumerPayoutMethodEditForm
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        record={selected}
      />
    </div>
  );
}

export default ConsumerPayoutMethodTable;
